import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Archive,
  ChevronRight,
  File as FileIcon,
  FileImage,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  Grid3X3,
  List,
  Pencil,
  RefreshCw,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import PageShell from '../components/PageShell';
import DataTable, { type Column } from '../components/DataTable';
import Modal from '../components/Modal';
import { primaryButtonClass, secondaryButtonClass, controlClass } from '../components/FilterBar';
import OpenInAppButton from '../components/OpenInAppButton';
import { formatDateTime } from '../lib/format';
import {
  deleteFile,
  fetchFiles,
  fileDownloadUrl,
  mkdirFile,
  moveFile,
  organizeZipFile,
  uploadFile,
  type FileListItem,
} from '../lib/api';

function formatSize(bytes?: number | null): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(name: string, type?: string | null) {
  if (type === 'collection') return <Folder size={18} className="text-zt-info" />;
  const lower = name.toLowerCase();
  if (/\.(png|jpe?g|gif|webp|svg|bmp)$/.test(lower)) {
    return <FileImage size={18} className="text-emerald-400" />;
  }
  if (/\.pdf$/.test(lower)) return <FileText size={18} className="text-red-400" />;
  if (/\.(zip|tar|gz|7z|rar)$/.test(lower)) return <Archive size={18} className="text-amber-400" />;
  return <FileIcon size={18} className="text-zt-muted" />;
}

function isPreviewable(name: string): boolean {
  const lower = name.toLowerCase();
  return /\.(pdf|png|jpe?g|gif|webp|svg)$/.test(lower);
}

function joinPath(base: string, name: string): string {
  return base ? `${base.replace(/\/$/, '')}/${name}` : name;
}

function Files() {
  const [path, setPath] = useState('');
  const [items, setItems] = useState<FileListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [connection, setConnection] = useState<{
    status: string;
    configured: boolean;
    connected: boolean;
    error?: string | null;
  }>({ status: 'ok', configured: true, connected: true });
  const [selected, setSelected] = useState<FileListItem | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [mkdirOpen, setMkdirOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async (targetPath: string) => {
    setLoading(true);
    const result = await fetchFiles(targetPath);
    if (result.ok && result.data) {
      setItems(result.data.items ?? []);
      setConnection({
        status: result.data.status ?? 'ok',
        configured: result.data.configured ?? true,
        connected: result.data.connected ?? true,
        error: result.data.error,
      });
    } else {
      setItems([]);
      setConnection({
        status: 'error',
        configured: false,
        connected: false,
        error: result.error?.message ?? 'Failed to load files',
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(path);
  }, [path, load]);

  const uploadOne = async (file: File) => {
    const targetPath = joinPath(path, file.name);
    const result = await uploadFile(targetPath, file);
    if (result.ok && result.data) {
      const data = result.data;
      if (data.organized && data.path) {
        toast.success(`Organized ${file.name} → ${data.path}`);
      } else if (data.documentType) {
        toast.success(`Uploaded ${file.name} (${data.documentType})`);
      } else {
        toast.success(`Uploaded ${file.name}`);
      }
      if (file.name.toLowerCase().endsWith('.zip')) {
        const org = await organizeZipFile(file, false);
        if (org.ok) toast.success('ZIP organize pipeline started');
      }
      load(path);
    }
  };

  const handleDrop = async (files: FileList | null) => {
    if (!files?.length) return;
    setDragOver(false);
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        await uploadOne(file);
      }
    } finally {
      setUploading(false);
    }
  };

  const openItem = (item: FileListItem) => {
    if (item.type === 'collection') {
      setPath(joinPath(path, item.name));
      setSelected(null);
    } else if (isPreviewable(item.name)) {
      setSelected(item);
    }
  };

  const handleMkdir = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    const target = joinPath(path, name);
    const result = await mkdirFile(target);
    if (result.ok) {
      toast.success(`Created folder ${name}`);
      setMkdirOpen(false);
      setNewFolderName('');
      load(path);
    }
  };

  const handleRename = async () => {
    if (!selected) return;
    const newName = renameValue.trim();
    if (!newName || newName === selected.name) return;
    const source = joinPath(path, selected.name);
    const destination = joinPath(path, newName);
    const result = await moveFile(source, destination);
    if (result.ok) {
      toast.success('Renamed');
      setRenameOpen(false);
      setSelected(null);
      load(path);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    const target = joinPath(path, selected.name);
    const result = await deleteFile(target);
    if (result.ok) {
      toast.success(`Deleted ${selected.name}`);
      setDeleteOpen(false);
      setSelected(null);
      load(path);
    }
  };

  const crumbs = path.split('/').filter(Boolean);
  const selectedPath = selected ? joinPath(path, selected.name) : null;
  const previewUrl = selectedPath ? fileDownloadUrl(selectedPath) : null;

  const columns: Column<FileListItem>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (r) => (
        <span className="flex items-center gap-2">
          {fileIcon(r.name, r.type)}
          {r.name}
        </span>
      ),
    },
    { key: 'type', header: 'Type', render: (r) => (r.type === 'collection' ? 'Folder' : 'File') },
    { key: 'size', header: 'Size', align: 'right', render: (r) => formatSize(r.size) },
    { key: 'modified', header: 'Modified', render: (r) => formatDateTime(r.modified) },
  ];

  const emptyTitle = !connection.configured
    ? 'oCIS not configured'
    : !connection.connected
      ? 'Cannot connect to oCIS'
      : 'This folder is empty';

  const emptyMessage = !connection.configured
    ? 'Set OCIS_SERVICE_BOT_USER, OCIS_SERVICE_BOT_PASSWORD, and OCIS_ACCOUNTING_SPACE_ROOT on the dashboard API.'
    : connection.error
      ? connection.error
      : 'Upload a file or create a folder to get started.';

  return (
    <PageShell
      title="Files"
      subtitle="Browse and manage files in the oCIS Accounting space."
    >
      <div
        className={[
          'rounded-xl border transition-colors',
          dragOver ? 'border-zt-primary bg-zt-primary/5' : 'border-transparent',
        ].join(' ')}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          handleDrop(e.dataTransfer.files);
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <nav className="flex flex-wrap items-center gap-1 text-sm">
            <button
              type="button"
              onClick={() => {
                setPath('');
                setSelected(null);
              }}
              className="flex items-center gap-1 rounded px-2 py-1 text-slate-200 hover:bg-zt-elevated"
            >
              <FolderOpen size={15} /> root
            </button>
            {crumbs.map((crumb, idx) => (
              <span key={idx} className="flex items-center gap-1">
                <ChevronRight size={14} className="text-zt-muted" />
                <button
                  type="button"
                  onClick={() => {
                    setPath(crumbs.slice(0, idx + 1).join('/'));
                    setSelected(null);
                  }}
                  className="rounded px-2 py-1 text-slate-200 hover:bg-zt-elevated"
                >
                  {crumb}
                </button>
              </span>
            ))}
          </nav>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-zt-border">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={[
                  'px-2 py-1.5',
                  viewMode === 'list' ? 'bg-zt-elevated text-zt-accent' : 'text-zt-muted',
                ].join(' ')}
                title="List view"
              >
                <List size={16} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={[
                  'px-2 py-1.5',
                  viewMode === 'grid' ? 'bg-zt-elevated text-zt-accent' : 'text-zt-muted',
                ].join(' ')}
                title="Grid view"
              >
                <Grid3X3 size={16} />
              </button>
            </div>
            <OpenInAppButton integrationKey="ocis" />
            <button type="button" onClick={() => setMkdirOpen(true)} className={secondaryButtonClass}>
              <FolderPlus size={16} /> New folder
            </button>
            <button type="button" onClick={() => load(path)} className={secondaryButtonClass}>
              <RefreshCw size={16} /> Refresh
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = e.target.files;
                if (files?.length) handleDrop(files);
              }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className={primaryButtonClass}
            >
              <Upload size={16} /> {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>

        {connection.status === 'error' && connection.error && (
          <div className="mt-4 flex items-start gap-3 rounded-lg border border-zt-danger/40 bg-zt-danger/10 p-4 text-sm">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-zt-danger" />
            <div>
              <p className="font-medium text-white">oCIS connection error</p>
              <p className="mt-1 text-zt-muted">{connection.error}</p>
            </div>
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
          <div>
            {viewMode === 'list' ? (
              <DataTable
                columns={columns}
                rows={items}
                rowKey={(r, i) => `${r.name}-${i}`}
                loading={loading}
                onRowClick={openItem}
                emptyTitle={emptyTitle}
                emptyMessage={emptyMessage}
                emptyIcon={<FolderOpen size={32} />}
              />
            ) : (
              <div className="min-h-[240px]">
                {loading ? (
                  <p className="p-8 text-center text-sm text-zt-muted">Loading…</p>
                ) : items.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-zt-border bg-zt-surface p-12 text-center">
                    <FolderOpen size={32} className="text-zt-muted" />
                    <p className="font-medium text-white">{emptyTitle}</p>
                    <p className="text-sm text-zt-muted">{emptyMessage}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                    {items.map((item, i) => (
                      <button
                        key={`${item.name}-${i}`}
                        type="button"
                        onClick={() => openItem(item)}
                        className={[
                          'flex flex-col items-center gap-2 rounded-xl border p-4 text-center transition-colors hover:bg-zt-elevated',
                          selected?.name === item.name
                            ? 'border-zt-primary bg-zt-primary/10'
                            : 'border-zt-border bg-zt-surface',
                        ].join(' ')}
                      >
                        {fileIcon(item.name, item.type)}
                        <span className="line-clamp-2 text-sm text-slate-200">{item.name}</span>
                        {item.type !== 'collection' && (
                          <span className="text-xs text-zt-muted">{formatSize(item.size)}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <aside className="rounded-xl border border-zt-border bg-zt-surface p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-medium text-white">Preview</h3>
              {selected && (
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="rounded p-1 text-zt-muted hover:text-slate-200"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            {!selected ? (
              <p className="text-sm text-zt-muted">Select a PDF or image to preview.</p>
            ) : (
              <div className="space-y-3">
                <p className="truncate text-sm font-medium text-white">{selected.name}</p>
                <p className="text-xs text-zt-muted">
                  {formatSize(selected.size)} · {formatDateTime(selected.modified)}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => { setRenameValue(selected.name); setRenameOpen(true); }} className={secondaryButtonClass}>
                    <Pencil size={14} /> Rename
                  </button>
                  <button type="button" onClick={() => setDeleteOpen(true)} className={secondaryButtonClass}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
                {previewUrl && isPreviewable(selected.name) ? (
                  selected.name.toLowerCase().endsWith('.pdf') ? (
                    <iframe
                      title={selected.name}
                      src={previewUrl}
                      className="h-64 w-full rounded border border-zt-border bg-white"
                    />
                  ) : (
                    <img
                      src={previewUrl}
                      alt={selected.name}
                      className="max-h-64 w-full rounded border border-zt-border object-contain"
                    />
                  )
                ) : (
                  <p className="text-xs text-zt-muted">No inline preview for this file type.</p>
                )}
              </div>
            )}
          </aside>
        </div>

        {dragOver && (
          <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <p className="rounded-xl border border-zt-primary bg-zt-surface px-8 py-4 text-lg text-white">
              Drop files to upload
            </p>
          </div>
        )}
      </div>

      <Modal
        open={mkdirOpen}
        onClose={() => setMkdirOpen(false)}
        title="New folder"
        footer={
          <>
            <button type="button" onClick={() => setMkdirOpen(false)} className={secondaryButtonClass}>
              Cancel
            </button>
            <button type="button" onClick={handleMkdir} className={primaryButtonClass}>
              Create
            </button>
          </>
        }
      >
        <input
          type="text"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          placeholder="Folder name"
          className={`w-full ${controlClass}`}
          onKeyDown={(e) => e.key === 'Enter' && handleMkdir()}
        />
      </Modal>

      <Modal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        title="Rename"
        footer={
          <>
            <button type="button" onClick={() => setRenameOpen(false)} className={secondaryButtonClass}>
              Cancel
            </button>
            <button type="button" onClick={handleRename} className={primaryButtonClass}>
              Rename
            </button>
          </>
        }
      >
        <input
          type="text"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          className={`w-full ${controlClass}`}
          onKeyDown={(e) => e.key === 'Enter' && handleRename()}
        />
      </Modal>

      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete item"
        description={`Permanently delete "${selected?.name}"? This cannot be undone.`}
        footer={
          <>
            <button type="button" onClick={() => setDeleteOpen(false)} className={secondaryButtonClass}>
              Cancel
            </button>
            <button type="button" onClick={handleDelete} className={primaryButtonClass}>
              Delete
            </button>
          </>
        }
      >
        <p className="text-sm text-zt-muted">Destructive actions are logged in the audit trail.</p>
      </Modal>
    </PageShell>
  );
}

export default Files;
